import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Palette, Download, Loader2, AlertTriangle } from 'lucide-react';
import {
    ARTE, LEFT_X, MAXW, FONTE, FONTE_TAMANHO, FONTE_ALTURA_LINHA,
    LINHA_VAGAS, LINHA_CONTATO, LINHA_ENDERECO,
    maxLinhasDe, topoDoValor, linhasVisiveis, mascaraTelefone,
} from './arte-layout';
import { ARTE_CONTRATACAO, carregarFontes, carregarImagem, baixarArquivo } from '../marketing-recursos';

// =====================================================================
// Marketing › GERADOR DE ARTE DE CONTRATAÇÃO — a arte de "estamos
// contratando" da loja, pronta para postar (Stories 1080×1920).
//
// O gerente escolhe a loja, marca as vagas abertas e informa quem é o contato;
// o resto (endereço, arte, diagramação) a tela resolve. O que sai é um PNG.
//
// ⚠️⚠️ A ARTE É UM ARQUIVO DE FUNDO COM RÓTULOS JÁ IMPRESSOS. Aqui só se
//    escrevem os VALORES, em coordenadas fixas — ver `arte-layout.js`, que
//    guarda o porquê de cada medida. Trocar a arte exige recalibrar aquilo.
//
// ⚠️ O ENDEREÇO VEM PREENCHIDO MAS CONTINUA EDITÁVEL: é o que permite anunciar
//    uma unidade nova (ou corrigir um complemento) sem esperar deploy.
// =====================================================================

const API = '/api/marketing';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#db2777';
const COR_ESCURA = '#9d174d';
const COR_CLARA = '#fdf2f8';
const COR_BORDA = '#fce7f3';

const rotulo = { display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 6 };
const campo = {
    width: '100%', padding: '10px 12px', fontSize: 14.5, color: '#0f172a',
    background: '#f8fafc', border: `1.5px solid #e2e8f0`, borderRadius: 10, outline: 'none',
};

const GeradorArteContratacao = () => {
    const canvasRef = useRef(null);
    const [fundo, setFundo] = useState(null);
    const [lojas, setLojas] = useState([]);
    const [cargos, setCargos] = useState([]);
    const [erro, setErro] = useState('');
    const [baixando, setBaixando] = useState(false);

    const [sigla, setSigla] = useState('');
    const [endereco, setEndereco] = useState('');
    const [nome, setNome] = useState('');
    const [telefone, setTelefone] = useState('');
    const [vagas, setVagas] = useState([]);

    // Arte + fontes + lista de lojas: tudo uma vez, na abertura.
    useEffect(() => {
        let vivo = true;
        (async () => {
            await carregarFontes([`400 ${FONTE_TAMANHO}px 'Space Grotesk'`]);
            try {
                const [imagem, resposta] = await Promise.all([
                    carregarImagem(ARTE_CONTRATACAO),
                    fetch(`${API}/lojas`, { headers: auth() }),
                ]);
                if (!resposta.ok) throw new Error('Não foi possível carregar as lojas.');
                const dados = await resposta.json();
                if (!vivo) return;
                setFundo(imagem);
                setLojas(dados.lojas);
                setCargos(dados.cargos);
            } catch (e) {
                if (vivo) setErro(e.message);
            }
        })();
        return () => { vivo = false; };
    }, []);

    const escolherLoja = (novaSigla) => {
        setSigla(novaSigla);
        const loja = lojas.find((l) => l.sigla === novaSigla);
        setEndereco(loja ? `${loja.linha1} — ${loja.linha2}` : '');
    };

    // ----- desenho -----
    const desenhar = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !fundo) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, ARTE.W, ARTE.H);
        ctx.drawImage(fundo, 0, 0, ARTE.W, ARTE.H);

        ctx.font = FONTE;
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        const medir = (texto) => ctx.measureText(texto).width;

        // Escreve as linhas de um valor logo abaixo do rótulo impresso na arte.
        const escrever = (linhas, faixa) => {
            const base = topoDoValor(faixa) + FONTE_TAMANHO * 0.86;
            linhas.forEach((linha, i) => ctx.fillText(linha, LEFT_X, base + i * FONTE_ALTURA_LINHA));
        };

        // VAGAS — uma por linha, com marcador. Um cargo que não couber numa
        // linha quebra normalmente; se a lista inteira não couber, os últimos
        // saem com reticências em vez de estourar o quadro vermelho.
        if (vagas.length) {
            const larguraMarcador = medir('• ');
            const linhas = vagas.flatMap((cargo) =>
                linhasVisiveis(medir, cargo, MAXW - larguraMarcador, 99)
                    .map((linha, i) => (i === 0 ? `• ${linha}` : `   ${linha}`)));
            const max = maxLinhasDe(LINHA_VAGAS);
            escrever(linhas.length > max
                ? linhasVisiveis(medir, linhas.slice(0, max).join(' '), MAXW, max)
                : linhas, LINHA_VAGAS);
        }

        // CONTATO — telefone e nome na mesma linha.
        const contato = [telefone.trim(), nome.trim()].filter(Boolean).join('  •  ');
        if (contato) escrever(linhasVisiveis(medir, contato, MAXW, maxLinhasDe(LINHA_CONTATO)), LINHA_CONTATO);

        // ENDEREÇO
        if (endereco.trim()) {
            escrever(linhasVisiveis(medir, endereco.trim(), MAXW, maxLinhasDe(LINHA_ENDERECO)), LINHA_ENDERECO);
        }
    }, [fundo, vagas, telefone, nome, endereco]);

    useEffect(() => { desenhar(); }, [desenhar]);

    const faltando = [
        !sigla && 'loja',
        !endereco.trim() && 'endereço',
        !nome.trim() && 'nome do contato',
        !telefone.trim() && 'telefone',
        !vagas.length && 'ao menos uma vaga',
    ].filter(Boolean);

    const baixar = () => {
        setBaixando(true);
        canvasRef.current.toBlob((blob) => {
            baixarArquivo(`vaga-${(sigla || 'loja').toLowerCase()}.png`, blob);
            setBaixando(false);
        }, 'image/png');
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                <div style={{ padding: 10, borderRadius: 12, background: COR_CLARA, color: COR }}>
                    <Palette size={22} />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                        Gerador de Arte de Contratação
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#64748b' }}>
                        A arte de "estamos contratando" da loja, pronta para postar — a prévia ao lado é o arquivo que sai.
                    </p>
                </div>
            </div>

            {erro && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'center',
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
                    padding: '12px 14px', color: '#991b1b', fontSize: 14, marginBottom: 20,
                }}><AlertTriangle size={16} /> {erro}</div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 440px) 1fr', gap: 28, alignItems: 'start' }}>
                {/* ---------------- formulário ---------------- */}
                <div style={{
                    background: '#fff', border: `1px solid ${COR_BORDA}`,
                    borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 18,
                }}>
                    <div>
                        <label style={rotulo} htmlFor="loja">Loja</label>
                        <select
                            id="loja"
                            value={sigla}
                            onChange={(e) => escolherLoja(e.target.value)}
                            style={campo}
                        >
                            <option value="">Selecione…</option>
                            {lojas.map((l) => (
                                <option key={l.sigla} value={l.sigla}>{l.sigla} — {l.linha2}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={rotulo} htmlFor="endereco">Endereço</label>
                        <input
                            id="endereco"
                            type="text"
                            value={endereco}
                            onChange={(e) => setEndereco(e.target.value)}
                            placeholder="Preenchido pela loja — corrija se precisar"
                            style={campo}
                        />
                        <p style={{ fontSize: 12.5, color: '#94a3b8', margin: '6px 0 0' }}>
                            Vem do cadastro da loja escolhida e pode ser ajustado aqui.
                        </p>
                    </div>

                    <div>
                        <label style={rotulo} htmlFor="nome">Nome do responsável pelo contato</label>
                        <input id="nome" type="text" value={nome} onChange={(e) => setNome(e.target.value)} style={campo} />
                    </div>

                    <div>
                        <label style={rotulo} htmlFor="telefone">Telefone</label>
                        <input
                            id="telefone"
                            type="tel"
                            value={telefone}
                            onChange={(e) => setTelefone(mascaraTelefone(e.target.value))}
                            placeholder="(27) 99999-9999"
                            style={campo}
                        />
                    </div>

                    <div>
                        <span style={rotulo}>Vagas abertas</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {cargos.map((cargo) => (
                                <label key={cargo} style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                    borderRadius: 9, cursor: 'pointer', fontSize: 14.5, color: '#0f172a',
                                    background: vagas.includes(cargo) ? COR_CLARA : 'transparent',
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={vagas.includes(cargo)}
                                        onChange={(e) => setVagas((antes) => (e.target.checked
                                            ? [...antes, cargo]
                                            : antes.filter((c) => c !== cargo)))}
                                        style={{ width: 17, height: 17, accentColor: COR }}
                                    />
                                    {cargo}
                                </label>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={baixar}
                        disabled={faltando.length > 0 || baixando || !fundo}
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            width: '100%', padding: '13px 18px', borderRadius: 12, border: 'none',
                            fontSize: 15, fontWeight: 800, color: '#fff',
                            cursor: faltando.length ? 'not-allowed' : 'pointer',
                            opacity: faltando.length ? 0.5 : 1,
                            background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
                        }}
                    >
                        {baixando ? <Loader2 size={17} /> : <Download size={17} />} Baixar a arte (PNG)
                    </button>

                    <p style={{
                        fontSize: 13, margin: 0, textAlign: 'center',
                        color: faltando.length ? '#b45309' : '#15803d', fontWeight: 600,
                    }}>
                        {faltando.length ? `Falta preencher: ${faltando.join(', ')}.` : 'Pronto! Confira a prévia e baixe.'}
                    </p>
                </div>

                {/* ---------------- prévia ---------------- */}
                <div style={{
                    background: '#fff', border: `1px solid ${COR_BORDA}`, borderRadius: 16,
                    padding: 16, display: 'flex', justifyContent: 'center',
                }}>
                    {/* O canvas tem o tamanho real da arte (3375×6000) e é só
                        encolhido por CSS — a prévia é o arquivo. */}
                    <canvas
                        ref={canvasRef}
                        width={ARTE.W}
                        height={ARTE.H}
                        style={{ width: '100%', maxWidth: 380, height: 'auto', borderRadius: 12, background: '#b01116' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default GeradorArteContratacao;
